import type { TAdvancePayment } from "components/AdvanceManagement/NewAdvanceModal";
import { storeAdvancePaymentNavigationTarget } from "helpers/advancePaymentNavigation";
import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { IAdvanceSlice } from "store/advancePaymentSlice";
import type { ITerm } from "store/managementStore";

interface UseAdvanceRecapDataParams {
    from: "form" | "drawer";
    term?: ITerm;
    termAdvancePayments?: IAdvanceSlice[];
    inlineAdvancePayment?: TAdvancePayment | null;
    titleEan13?: string;
    advancePaymentId?: number | null;
}

export const useAdvanceRecapData = ({
    from,
    term,
    termAdvancePayments,
    inlineAdvancePayment,
    titleEan13,
    advancePaymentId,
}: UseAdvanceRecapDataParams) => {
    const navigate = useNavigate();

    const handleGoToAdvancePayment = useCallback(() => {
        storeAdvancePaymentNavigationTarget({ titleEan13, paymentId: advancePaymentId });
        navigate("/advance-payment");
    }, [titleEan13, advancePaymentId, navigate]);

    const drawerAdvanceSlices = useMemo(() => {
        if (from !== "drawer" || !termAdvancePayments?.length || !term?.advancePaymentId) return null;
        const linked = termAdvancePayments.find((p) => p.id === term.advancePaymentId);
        if (!linked?.slices?.length) return null;
        return linked.slices.map((s) => ({
            amount: Math.abs(s.amount),
            paymentDate: s.paymentDate,
            status: s.status as 0 | 1 | 2,
            comment: s.comment ?? null,
        }));
    }, [from, termAdvancePayments, term?.advancePaymentId]);

    // Amounts in cents → convert to euros
    const inlineAdvanceTotal = useMemo(() => {
        if (!inlineAdvancePayment?.slices?.length) return null;
        return inlineAdvancePayment.slices.reduce((sum, s) => sum + +s.amount, 0) / 100;
    }, [inlineAdvancePayment]);

    // Amounts already in euros (from API via Math.abs above)
    const drawerAdvanceTotal = useMemo(() => {
        if (!drawerAdvanceSlices?.length) return null;
        return drawerAdvanceSlices.reduce((sum, s) => sum + s.amount, 0) / 100;
    }, [drawerAdvanceSlices]);

    return {
        drawerAdvanceSlices,
        inlineAdvanceTotal,
        drawerAdvanceTotal,
        handleGoToAdvancePayment,
    };
};
